// Bir urunu SECILI MAGAZALARA gonder.
//
// Her magaza kendi stratejisiyle uyarlanir; bu yuzden onizlemede magaza basina
// FARKLI baslik/fiyat/gorsel sayisi gorebilirsiniz. Gondermeden once ön kontrol
// (uyari/engel) calisir; bir magaza engelliyse digerleri yine de gider.

import { useEffect, useState } from "react";
import {
  X, Loader2, Send, AlertTriangle, XCircle, CheckCircle, Store,
} from "lucide-react";
import { clsx } from "clsx";
import { authHeaders, getBackendUrl } from "../../lib/http";
import { useShopStore } from "../../store/shop.store";

interface PreflightRow {
  shopId: string;
  shopName: string;
  platform: string;
  strategyId?: string | null;
  preview?: { title: string; tagCount: number; imageCount: number; price: number; currency: string };
  warnings: string[];
  blockers: string[];
}

interface PublishResult {
  queued: Array<{ shopId: string; publicationId: string; jobId: string }>;
  rejected: Array<{ shopId: string; shopName: string; reason: string }>;
}

export function PublishToShopsModal({
  productId, productTitle, onClose,
}: {
  productId: string;
  productTitle: string;
  onClose: () => void;
}) {
  const { shops, loadShops } = useShopStore();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [state, setState] = useState<"draft" | "active">("draft");
  const [rows, setRows] = useState<PreflightRow[]>([]);
  const [checking, setChecking] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<PublishResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const activeShops = shops.filter((s) => s.isActive);

  useEffect(() => { loadShops(); }, [loadShops]);

  async function runPreflight(ids: string[]) {
    if (ids.length === 0) { setRows([]); return; }
    setChecking(true);
    setError(null);
    try {
      const res = await fetch(`${getBackendUrl()}/api/publications/${productId}/preflight`, {
        method: "POST", headers: authHeaders(), body: JSON.stringify({ shopIds: ids }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || "Ön kontrol başarısız");
      setRows(body.data.results as PreflightRow[]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Ön kontrol başarısız");
    } finally {
      setChecking(false);
    }
  }

  function toggle(shopId: string) {
    const next = new Set(selected);
    if (next.has(shopId)) next.delete(shopId); else next.add(shopId);
    setSelected(next);
    setResult(null);
    runPreflight([...next]);
  }

  async function publish() {
    setSending(true);
    setError(null);
    try {
      const res = await fetch(`${getBackendUrl()}/api/publications/${productId}/publish`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ shopIds: [...selected], state }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || "Gönderilemedi");
      setResult(body.data as PublishResult);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Gönderilemedi");
    } finally {
      setSending(false);
    }
  }

  const hasBlockers = rows.some((r) => selected.has(r.shopId) && r.blockers.length > 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b border-gray-100 p-5">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <Store className="h-5 w-5 text-indigo-600" /> Mağazalara Gönder
            </h2>
            <p className="mt-0.5 line-clamp-1 text-xs text-gray-500">{productTitle}</p>
          </div>
          <button onClick={onClose} className="rounded p-1 text-gray-400 hover:text-gray-600">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto p-5">
          <p className="text-xs text-gray-500">
            Her mağaza kendi stratejisiyle uyarlanır — başlık, fiyat ve görsel sayısı
            mağazadan mağazaya farklı olabilir.
          </p>

          {activeShops.length === 0 && (
            <div className="rounded-lg border border-dashed border-gray-200 p-6 text-center text-sm text-gray-500">
              Aktif mağaza yok. Ayarlar'dan mağaza bağlayın.
            </div>
          )}

          {activeShops.map((shop) => {
            const row = rows.find((r) => r.shopId === shop.id);
            const isOn = selected.has(shop.id);
            return (
              <div
                key={shop.id}
                className={clsx(
                  "rounded-lg border p-3 transition-colors",
                  isOn ? "border-indigo-300 bg-indigo-50/30" : "border-gray-200"
                )}
              >
                <label className="flex cursor-pointer items-center gap-2">
                  <input type="checkbox" checked={isOn} onChange={() => toggle(shop.id)} className="h-4 w-4" />
                  <span
                    className="h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ background: shop.color || "#6366f1" }}
                  />
                  <span className="text-sm font-medium text-gray-900">{shop.shopName}</span>
                  <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-600">{shop.platform}</span>
                  {shop.label && <span className="text-[11px] text-gray-400">{shop.label}</span>}
                </label>

                {isOn && row?.preview && (
                  <div className="mt-2 space-y-1 border-t border-gray-100 pt-2 text-[11px] text-gray-600">
                    <p><span className="text-gray-400">Başlık:</span> {row.preview.title}</p>
                    <p>
                      <span className="text-gray-400">Fiyat:</span> {row.preview.price} {row.preview.currency}
                      {" · "}<span className="text-gray-400">Etiket:</span> {row.preview.tagCount}
                      {" · "}<span className="text-gray-400">Görsel:</span> {row.preview.imageCount}
                      {!row.strategyId && <span className="ml-1 text-gray-400">(strateji yok — varsayılan)</span>}
                    </p>
                  </div>
                )}

                {isOn && row?.warnings.map((w, i) => (
                  <p key={i} className="mt-1 flex items-start gap-1 text-[11px] text-amber-700">
                    <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" /> {w}
                  </p>
                ))}
                {isOn && row?.blockers.map((b, i) => (
                  <p key={i} className="mt-1 flex items-start gap-1 text-[11px] text-red-600">
                    <XCircle className="mt-0.5 h-3 w-3 shrink-0" /> {b}
                  </p>
                ))}
              </div>
            );
          })}

          {checking && (
            <p className="flex items-center gap-1.5 text-xs text-gray-500">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Ön kontrol yapılıyor...
            </p>
          )}
          {error && <p className="text-xs text-red-600">{error}</p>}

          {result && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs">
              {result.queued.length > 0 && (
                <p className="flex items-center gap-1.5 text-emerald-700">
                  <CheckCircle className="h-3.5 w-3.5" />
                  {result.queued.length} mağaza kuyruğa alındı — yayın arka planda tamamlanır
                </p>
              )}
              {result.rejected.map((r) => (
                <p key={r.shopId} className="mt-1 flex items-start gap-1.5 text-red-600">
                  <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  {r.shopName}: {r.reason}
                </p>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-gray-100 bg-gray-50 px-5 py-4">
          <label className="flex items-center gap-1.5 text-xs text-gray-600">
            <input
              type="checkbox"
              checked={state === "active"}
              onChange={(e) => setState(e.target.checked ? "active" : "draft")}
              className="h-3.5 w-3.5"
            />
            Doğrudan yayına al (işaretsizse taslak olarak gönderilir)
          </label>

          <div className="flex gap-2">
            <button onClick={onClose} className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600 hover:bg-white">
              Kapat
            </button>
            <button
              onClick={publish}
              disabled={selected.size === 0 || sending || checking || hasBlockers}
              title={hasBlockers ? "Engelleri giderin" : undefined}
              className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-40"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {selected.size > 0 ? `${selected.size} mağazaya gönder` : "Mağaza seçin"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
